/**
 * Contains basic attributes shared between almost all different elements
 */
const attrs = {
    accesskey: "accessKey",
    class: "className",
    contenteditable: "contentEditable",
    contextmenu: "contextmenu",
    dir: "dir",
    draggable: "draggable",
    dropzone: "dropzone",
    hidden: "hidden",
    id: "id",
    itemprop: "itemProp",
    itemscope: "itemScope",
    itemtype: "itemType",
    lang: "lang",
    slot: "slot",
    spellcheck: "spellcheck",
    tabindex: "tabIndex",
    title: "title",
};

/**
 * Basic attributes plus "align" attribute, which is used on ~10 elements
 */
const attrsPlusAlign = {
    ...attrs,
    align: "align",
};

/**
 * Shared attributes for elements linking somewhere
 */
const linkAttrs = {
    download: "download",
    href: "href",
    hreflang: "hreflang",
    media: "media",
    ping: "ping",
    rel: "rel",
    shape: "shape",
    target: "target",
};

/**
 * Shared attributes for elements that accept a size
 */
const sizeAttrs = {
    height: "height",
    width: "width",
};

/**
 * Shared attributes for media elements (audio / video)
 */
const mediaAttrs = {
    autoplay: "autoplay",
    buffered: "buffered",
    controls: "controls",
    crossorigin: "crossOrigin",
    loop: "loop",
    muted: "muted",
    preload: "preload",
    src: "src",
};

/**
 * Combined map of all supported elements, including maps of all their supported
 * attributes and the related names used by react.
 *
 * We're going to use these data to easily parse plain html elements into their
 * corresponding react nodes.
 */
export const elementAttributes: Readonly<{
    [key: string]: Readonly<{
        [key: string]: string;
    }>;
}> = {
    a: {
        ...attrs,
        ...linkAttrs,
    },
    abbr: attrs,
    address: attrs,
    area: {
        ...attrs,
        ...linkAttrs,
        alt: "alt",
        coords: "coords",
    },
    article: attrs,
    aside: attrs,
    audio: {
        ...attrs,
        ...mediaAttrs,
        ...sizeAttrs,
    },
    b: attrs,
    bdi: attrs,
    bdo: attrs,
    big: attrs,
    blockquote: {
        ...attrs,
        cite: "cite",
    },
    br: {},
    button: {
        ...attrs,
        autofocus: "autofocus",
        disabled: "disabled",
        form: "form",
        formaction: "formAction",
        name: "name",
        type: "type",
        value: "value",
    },
    caption: attrsPlusAlign,
    cite: attrs,
    code: attrs,
    col: {
        ...attrsPlusAlign,
        span: "span",
    },
    colgroup: {
        ...attrsPlusAlign,
        span: "span",
    },
    data: attrs,
    datalist: attrs,
    dd: attrs,
    del: {
        ...attrs,
        cite: "cite",
        datetime: "dateTime",
    },
    details: {
        ...attrs,
        open: "open",
    },
    dfn: attrs,
    dialog: attrs,
    dir: attrs,
    div: attrs,
    dl: attrs,
    dt: attrs,
    em: attrs,
    embed: {
        ...attrs,
        ...sizeAttrs,
        src: "src",
        type: "type",
    },
    fieldset: {
        ...attrs,
        disabled: "disabled",
        form: "form",
        name: "name",
    },
    figcaption: attrs,
    figure: attrs,
    footer: attrs,
    form: {
        ...attrs,
        accept: "accept",
        "accept-charset": "acceptCharset",
        action: "action",
        autocomplete: "autocomplete",
        enctype: "enctype",
        method: "method",
        name: "name",
        novalidate: "noValidate",
        target: "target",
    },
    h1: attrs,
    h2: attrs,
    h3: attrs,
    h4: attrs,
    h5: attrs,
    h6: attrs,
    header: attrs,
    hgroup: attrs,
    hr: attrsPlusAlign,
    i: attrs,
    iframe: {
        ...attrsPlusAlign,
        ...sizeAttrs,
        name: "name",
        sandbox: "sandbox",
        seamless: "seamless",
        src: "src",
        srcdoc: "srcdoc",
    },
    img: {
        ...attrsPlusAlign,
        ...sizeAttrs,
        alt: "alt",
        crossorigin: "crossOrigin",
        ismap: "isMap",
        sizes: "sizes",
        src: "src",
        srcset: "srcset",
        usemap: "useMap",
    },
    input: {
        ...attrs,
        ...sizeAttrs,
        accept: "accept",
        alt: "alt",
        autocomplete: "autocomplete",
        autofocus: "autofocus",
        checked: "checked",
        dirname: "dirName",
        disabled: "disabled",
        form: "form",
        formaction: "formAction",
        list: "list",
        max: "max",
        maxlength: "maxLength",
        min: "min",
        minlength: "minLength",
        multiple: "multiple",
        name: "name",
        pattern: "pattern",
        placeholder: "placeholder",
        readonly: "readOnly",
        required: "required",
        size: "size",
        src: "src",
        step: "step",
        type: "type",
        usemap: "useMap",
        value: "value",
    },
    ins: {
        ...attrs,
        cite: "cite",
        datetime: "dateTime",
    },
    kdb: attrs,
    keygen: {
        ...attrs,
        autofocus: "autofocus",
        challenge: "challenge",
        disabled: "disabled",
        form: "form",
        keytype: "keytype",
        name: "name",
    },
    label: {
        ...attrs,
        for: "htmlFor",
        form: "form",
    },
    legend: attrs,
    li: {
        ...attrs,
        value: "value",
    },
    main: attrs,
    map: {
        ...attrs,
        name: "name",
    },
    mark: attrs,
    menu: {
        ...attrs,
        type: "type",
    },
    menuitem: attrs,
    meter: {
        ...attrs,
        form: "form",
        high: "high",
        low: "low",
        max: "max",
        min: "min",
        optimum: "optimum",
        value: "value",
    },
    nav: attrs,
    noembed: attrs,
    object: {
        ...attrs,
        ...sizeAttrs,
        data: "data",
        form: "form",
        name: "name",
        type: "type",
        usemap: "useMap",
    },
    ol: {
        ...attrs,
        reversed: "reversed",
        start: "start",
    },
    optgroup: {
        ...attrs,
        disabled: "disabled",
        label: "label",
    },
    option: {
        ...attrs,
        disabled: "disabled",
        selected: "selected",
        value: "value",
    },
    output: {
        ...attrs,
        for: "htmlFor",
        form: "form",
        name: "name",
    },
    p: attrs,
    param: {
        ...attrs,
        name: "name",
        value: "value",
    },
    picture: attrs,
    pre: attrs,
    progress: {
        ...attrs,
        form: "form",
        max: "max",
        value: "value",
    },
    q: {
        ...attrs,
        cite: "cite",
    },
    rp: attrs,
    rt: attrs,
    rtc: attrs,
    ruby: attrs,
    s: attrs,
    samp: attrs,
    section: attrs,
    select: {
        ...attrs,
        autofocus: "autofocus",
        disabled: "disabled",
        form: "form",
        multiple: "multiple",
        name: "name",
        required: "required",
        size: "size",
    },
    small: attrs,
    source: {
        ...attrs,
        media: "media",
        sizes: "sizes",
        src: "src",
        type: "type",
    },
    span: attrs,
    strong: attrs,
    sub: attrs,
    summary: attrs,
    sup: attrs,
    table: {
        ...attrsPlusAlign,
        summary: "summary",
    },
    tbody: attrsPlusAlign,
    td: {
        ...attrsPlusAlign,
        colspan: "colSpan",
        headers: "headers",
        rowSpan: "rowSpan",
    },
    textarea: {
        ...attrs,
        autofocus: "autofocus",
        cols: "cols",
        dirname: "dirName",
        disabled: "disabled",
        form: "form",
        maxlength: "maxLength",
        minlength: "minLength",
        name: "name",
        placeholder: "placeholder",
        readonly: "readOnly",
        required: "required",
        rows: "rows",
        wrap: "wrap",
    },
    tfoot: attrsPlusAlign,
    th: {
        ...attrsPlusAlign,
        colspan: "colSpan",
        headers: "headers",
        rowSpan: "rowSpan",
        scope: "scope",
    },
    thead: attrsPlusAlign,
    time: {
        ...attrs,
        datetime: "dateTime",
    },
    tr: attrsPlusAlign,
    track: {
        ...attrs,
        default: "default",
        kind: "kind",
        label: "label",
        src: "src",
        srclang: "srclang",
    },
    u: attrs,
    ul: attrs,
    var: attrs,
    video: {
        ...attrs,
        ...mediaAttrs,
        ...sizeAttrs,
        poster: "poster",
    },
    wbr: {},
};
